/**
 * Returning patients: name and date of birth, then a 6-digit code sent only
 * to the phone or email on file (OB-08). The screen reads the same whether or
 * not we found a record; the code is the only proof.
 */
import { TURNSTILE_ACTIONS, type TurnstilePublic } from '@velora/contracts/turnstile';
import { Button, Field, Input } from '@velora/ui';
import { ArrowLeft, KeyRound } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { messageOf, type BookingApi } from '../lib/api.js';
import { formatDobInput, parseDob } from '../lib/format.js';
import { BOT_WAIT_MESSAGE, BotCheck, botTokenMissing, type TurnstileHandle } from './Turnstile.js';

interface Props {
  api: BookingApi;
  today: string;
  /** Bot check for "Send my code" (it sends a text). */
  bot?: TurnstilePublic | undefined;
  onVerified: (token: string) => void;
  onCancel: () => void;
}

export function Identify({ api, today, bot, onVerified, onCancel }: Props) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [dob, setDob] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<{ id: string; message: string } | null>(null);
  const [code, setCode] = useState('');
  const [botToken, setBotToken] = useState<string | null>(null);
  const botWidget = useRef<TurnstileHandle>(null);

  const dobIso = parseDob(dob, today);
  const bad = {
    first: first.trim() === '',
    last: last.trim() === '',
    dob: dobIso === null,
  };

  const ask = async (e?: FormEvent) => {
    e?.preventDefault();
    setTried(true);
    if (bad.first || bad.last || !dobIso) return;
    if (botTokenMissing(bot, botToken)) {
      setError(BOT_WAIT_MESSAGE);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const c = await api.identify({
        firstName: first.trim(),
        lastName: last.trim(),
        birthdate: dobIso,
        ...(botToken ? { turnstileToken: botToken } : {}),
      });
      setChallenge({ id: c.challengeId, message: c.message });
      setCode('');
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
      if (botToken) botWidget.current?.reset();
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!challenge || !/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onVerified(await api.verify(challenge.id, code));
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bw-identify" aria-labelledby="bw-identify-title">
      <header className="bw-identify__head">
        <span className="bw-identify__icon" aria-hidden>
          <KeyRound size={18} />
        </span>
        <div>
          <h3 id="bw-identify-title">Find your record</h3>
          <p className="bw-dim">
            {challenge
              ? challenge.message
              : 'We will send a one-time code to the mobile number or email we have on file.'}
          </p>
        </div>
      </header>

      {challenge ? (
        <form className="bw-identify__form" onSubmit={(e) => void verify(e)} noValidate>
          <Field label="6-digit code" required>
            <Input
              className="bw-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              aria-describedby="bw-identify-title"
            />
          </Field>
          {error ? (
            <p className="bw-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="bw-row">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={busy}
              disabled={code.length !== 6}
            >
              Continue
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              onClick={() => void ask()}
              disabled={busy}
            >
              Send a new code
            </Button>
          </div>
        </form>
      ) : (
        <form className="bw-identify__form" onSubmit={(e) => void ask(e)} noValidate>
          <div className="bw-grid2">
            <Field
              label="First name"
              required
              error={tried && bad.first ? 'Enter your first name.' : undefined}
            >
              <Input
                autoComplete="given-name"
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                maxLength={60}
              />
            </Field>
            <Field
              label="Last name"
              required
              error={tried && bad.last ? 'Enter your last name.' : undefined}
            >
              <Input
                autoComplete="family-name"
                value={last}
                onChange={(e) => setLast(e.target.value)}
                maxLength={60}
              />
            </Field>
          </div>
          <Field
            label="Date of birth"
            required
            hint="MM/DD/YYYY"
            error={tried && bad.dob ? 'Enter a real date as MM/DD/YYYY.' : undefined}
          >
            <Input
              inputMode="numeric"
              autoComplete="bday"
              placeholder="MM/DD/YYYY"
              value={dob}
              onChange={(e) => setDob(formatDobInput(e.target.value))}
            />
          </Field>
          <BotCheck
            ref={botWidget}
            bot={bot}
            action={TURNSTILE_ACTIONS.bookingIdentify}
            onToken={setBotToken}
          />
          {error ? (
            <p className="bw-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="bw-row">
            <Button type="submit" variant="primary" size="lg" loading={busy}>
              Send my code
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              leadingIcon={<ArrowLeft size={16} />}
              onClick={onCancel}
            >
              I am new here
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
